import { TAcquisitionProviderFacade } from "../../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";
import { ErrorEntry, FunctionPipe, Pipe, PipeResult } from "../../../utils/FunctionPipe";
import { hasDefaultComponent } from "../../../utils/pageModelUtils";
import { isValidEmailFormat, regexValidation } from "../../../utils/cob-utils";
import { AcquisitionFlow, isSamePersonCheckFromFacade } from "../../../utils/acquisition-utils";
import useBriteVerify from "../../../hooks/useBriteVerify";
import { usePushdownContext } from "../../../providers/Pushdown/PushdownProvider";
import { stateZipCombinationValidation } from "./useApplicationCtaFormValidations-PassiveValidations";

// TODO: Derive this type from all of the Component Names exported in the index.js of each component
export type TApplicationCtaComponentNameMap =
        | 'application-esign'
        | 'authorized-user'
        | 'credit-protection'
        | 'consumer-report'
        | 'personal-information-form'
        | 'phone-number-and-income'
        | 'plastic-card-design'
        | 'ssn-module'
        | 'terms-and-conditions';

export class ApplicationCtaValidationError extends Error {
    public validatingState: TAcquisitionProviderFacade;
    public errors: string[];

    private _abortApplication: string;

    constructor({
        validatingState,
        errors,
        abortApplication
    } : {
        validatingState: TAcquisitionProviderFacade,
        errors: string | string[] | ErrorEntry[] | Record<string, string>
        abortApplication?: string;
    }) {
        super();
        this.validatingState = validatingState;
        this._abortApplication = abortApplication;

        if (Array.isArray(errors)) {
            if (typeof errors[0] === 'string') {
                this.errors = errors as string[];
            } else if (Object.hasOwn(errors[0], 'index')) {
                this.errors = (errors as ErrorEntry[]).map((entry) => `${entry.nameOfPipe} :: ${entry.index} :: ${entry.error}`)
            } else {
                console.log(errors);
            }
        } else if (typeof errors === 'string') {
            this.errors = [errors];
        } else {
            this.errors = Object.entries(errors).map((entry => `[${entry[0]}] :: ${entry[1]}`));
        }
    }

    get cause(): string {
        return this.errors.join(' ; ');
    }

    /**
     * Human Readable Error Message Stack
     */
    get message() {
        return 'ERRORS ::\n' + this.errors?.join('\n');
    }

    get isApplicationAborted() {
        return !!this._abortApplication;
    }

    get abortApplication(): string {
        return this._abortApplication ? this._abortApplication : '';
    }

};

const useApplicationCtaFormValidations = (): {
    createValidationPipeline: (stateToValidate: TAcquisitionProviderFacade, componentName: string) => Pipe<TAcquisitionProviderFacade>,
    performActiveValidations: (validatingState: TAcquisitionProviderFacade) => Promise<TAcquisitionProviderFacade>,
    getPassiveValidations: () => Array<[string, (pipe: Pipe<TAcquisitionProviderFacade>) => Promise<PipeResult<unknown>>]>,
    checkPassiveValidation: (validatingState: TAcquisitionProviderFacade) => Promise<TAcquisitionProviderFacade>,
    getAllValidations: () => ({[name: string]: ((pipe: Pipe<TAcquisitionProviderFacade>) => Promise<PipeResult<unknown>>)}),
} => {

    const { showPushdown } = usePushdownContext();
    const { emailValidationApi, phoneValidationApi } = useBriteVerify();
    const { allow, zip_code, city } = regexValidation();

    const createValidationPipeline = (
        stateToValidate: TAcquisitionProviderFacade,
        componentName: string,
    ): Pipe<TAcquisitionProviderFacade> => FunctionPipe.pipe<TAcquisitionProviderFacade>(stateToValidate, {nameOfPipe: componentName});

    const creditProtectionValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .validate((stateToValidate) => ({ valid: true, exitEarly: stateToValidate.getCreditProtectionInfo().CreditProtectionSelected === false }))
        .validate((stateToValidate) => ({ valid: stateToValidate.getIsCreditProtectionValid() || false }));

    const applicationEsignValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .transform((stateToValidate: TAcquisitionProviderFacade) => stateToValidate.getEsign())
        .validate((esignValues) => {
            const isValid = (
                !esignValues.isEsignRequired
                || (esignValues.isEsignAgreed && esignValues.isEsignShowing)
            );
            return ({ valid: isValid, error: !isValid ? 'Failed Application Esign Validation' : undefined });
        });

    const personalInformationValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .validate((stateToValidate) => {
            // Validate Field State and Error States.
            return ({ valid: stateToValidate.validatePersonalInformation() || false });

        }).validate((stateToValidate) => {
            // Validate Loqate Semaphore
            const validationRefState = stateToValidate.getValidationRef()?.current;
            if (!validationRefState) {
                return ({ valid: true });
            }

            const personalInfoFormState = stateToValidate.getPersonalInfoForm();
            if (
                (!validationRefState.isValid && validationRefState.data)
                || (validationRefState.isValid && validationRefState.data.commercial)
                && (
                    personalInfoFormState.homeAddress.value &&!allow.home_address.test(personalInfoFormState.homeAddress.value)
                    || (personalInfoFormState.homeCity.value && !city.test(personalInfoFormState.homeCity.value))
                    || (personalInfoFormState.homeZip.value && !zip_code.test(personalInfoFormState.homeZip.value))
                    || (!personalInfoFormState.homeState.value)
                )
            ) {
                return ({ valid: false, error: 'loqate home error' });
            }
            return ({ valid: true });
        });

    const phoneNumberAndIncomeValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .validate((stateToValidate) => ({ valid: stateToValidate.getIsPhoneAndIncomeValid() || false }));
        
    const plasticCardDesignValidation = async (pipe: Pipe<TAcquisitionProviderFacade>) => await pipe
        .validate((stateToValidate) => {
            const cardSelected = !!stateToValidate.getSelectedCardId();
            return ({ valid: cardSelected || false, error: cardSelected ? undefined : 'SelectedCardId Missing' })
        });

    const ssnModuleValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .transform((stateToValidate: TAcquisitionProviderFacade) => stateToValidate.getSsn())
        .validate((ssnValue) => ({ valid: ssnValue.isSsnInfoValid && ssnValue?.ssnInfo?.length === 9 }));

    const termsAndConditions = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .transform((stateToValidate: TAcquisitionProviderFacade) => stateToValidate.getTerms())
        .validate((termsValues) => ({ valid: termsValues.isTermsAgreed && termsValues.isTermsValid }));

    const authorisedUserValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .validate((statetoValidate) => {
            return {
                valid: true,
                exitEarly: statetoValidate.getAuthorizedUser().authorizedUserInfo.IsAuthorizedUserSelected === false,
            };
        })
        .validate(isSamePersonCheckFromFacade)
        .transform((stateToValidate: TAcquisitionProviderFacade) => stateToValidate.getAuthorizedUser())
        .validate((authorisedUserValues) => ({ valid: authorisedUserValues.isAuthorisedUserSectionValid }));

    const consumerReportValidation = async (pipe: Pipe<TAcquisitionProviderFacade>): Promise<PipeResult<unknown>> => await pipe
        .validate((stateToValidate) => ({ valid: stateToValidate.getConsumerReport()?.isConsumerReportAgreed || false }));


    const ComponentNameMap: {
        [name: string]: ((pipe: Pipe<TAcquisitionProviderFacade>) => Promise<PipeResult<unknown>>)
    } = {
        'application-esign': applicationEsignValidation,
        'authorized-user': authorisedUserValidation,
        'credit-protection': creditProtectionValidation,
        'consumer-report': consumerReportValidation,
        'personal-information-form': personalInformationValidation,
        'phone-number-and-income': phoneNumberAndIncomeValidation,
        'plastic-card-design': plasticCardDesignValidation,
        'ssn-module': ssnModuleValidation,
        'terms-and-conditions': termsAndConditions,
    };

    /**
     * Performs full validations required to submit.
     * Runs to validate if the Form is Completed
     * 
     * If successful, returns the same state it was called with.
     * If unsuccessful, throws an instance of ApplicationCtaValidationError
     * 
     * @param validatingState 
     * @returns validatingState
     */
    const performActiveValidations = async (validatingState: TAcquisitionProviderFacade): Promise<TAcquisitionProviderFacade> => {
        const queuedValidations = Object.entries(ComponentNameMap)
            .map(([componentName, validationFunction]) => {
                if (hasDefaultComponent(`cob-corp-acquisition/components/creditonebank/${componentName}`)) {
                    // Promise in progress is returned, not the result.
                    return validationFunction(createValidationPipeline(validatingState, componentName));
                }
                return null;
            }).filter((entry) => entry !== null);

        const completedValidations = await Promise.all(queuedValidations);

        const compiledErrors = 
            completedValidations
                .filter((result) => !result.isSuccess())
                .map((entry) => entry.getDebugString());

        if (Object.keys(compiledErrors).length > 0) {
            throw new ApplicationCtaValidationError({
                errors: compiledErrors,
                validatingState,
            });
        } else {
            return validatingState;
        }
    };

    const getCurrentValidations = (): Array<[string, typeof ComponentNameMap[keyof typeof ComponentNameMap]]> => {
        return Object.entries(ComponentNameMap).filter(([componentName]) => hasDefaultComponent(`cob-corp-acquisition/components/creditonebank/${componentName}`));
    };

    /**
     * Performs first pass validations before attempting to submit.
     * Runs before Form is completed
     * 
     * If successful, returns the same state it was called with.
     * If unsuccessful, throws an instance of ApplicationCtaValidationError
     * 
     * @param validatingState 
     * @returns validatingState
     */
    const checkPassiveValidation = async (validatingState: TAcquisitionProviderFacade): Promise<TAcquisitionProviderFacade> => {
        const {
            emailAddress,
            phoneNumber,
            mailingAddressCity,
            mailingAddressZipCode,
        } = validatingState.getPersonalInformation();

        const {
            flow,
            isAdobeMode,
            isAuthor,
            useEmailApi,
            usePhoneApi,
        } = validatingState.getCurrentMetaAcquisitionFlowState();

        const isEmailInError: boolean = 
            emailAddress.value
            && ((!isAuthor && !isAdobeMode) || (isAdobeMode && useEmailApi)) 
            && flow !== AcquisitionFlow.PqOfOne 
            && isValidEmailFormat(emailAddress.value)
            && !(await emailValidationApi(emailAddress.value));

        const isPhoneInError: boolean = 
            phoneNumber.value
            && ((!isAuthor && !isAdobeMode) || (isAdobeMode && usePhoneApi)) 
            && !(await phoneValidationApi(phoneNumber.value.replace(/[^0-9]/g, '').slice(0, 10)));

        const didStateZipFail: string = 
            mailingAddressZipCode.value !== '' 
            && mailingAddressCity.value !== ''
            && (await stateZipCombinationValidation(showPushdown, validatingState));

        if (
            isEmailInError 
            || isPhoneInError 
            || didStateZipFail
        ) {
            throw new ApplicationCtaValidationError({ 
                errors: [
                    'Failed Passive Validations',
                    `isEmailInError: ${isEmailInError}`,
                    `isPhoneInError: ${isPhoneInError}`,
                    `didStateZipFail: ${didStateZipFail}`,
                ],
                abortApplication: 
                    didStateZipFail === 'Server Side Failure'
                        ? didStateZipFail
                        : undefined,
                validatingState,
            });
        }

        return validatingState;
    }

    const getAllValidations = (): typeof ComponentNameMap => {
        return ComponentNameMap;
    };

    return {
        createValidationPipeline,
        performActiveValidations,
        getPassiveValidations: getCurrentValidations,
        checkPassiveValidation,
        getAllValidations,
    };
};


export default useApplicationCtaFormValidations;